import { RobotOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { Avatar, Space, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { SECTIONS, sectionSurface } from "@/shared/config";
import { formatMoney, formatPercent } from "@/shared/lib";
import { SectionCard } from "@/shared/ui";

/**
 * Как карточка робота выглядит у пользователя (ТЗ §32).
 *
 * Повторяет `miniapp/src/entities/robot/ui/RobotListItem.tsx`, включая его
 * правила: строка про AI появляется только при непустом наборе сигналов,
 * комиссия — только когда она есть, скрытые монеты не показываются. Иначе
 * превью врало бы ровно там, где оператор его и открывает — чтобы понять, что
 * увидит пользователь.
 *
 * Приблизительность оговорена в подзаголовке: шрифты и отступы в мини-аппе
 * свои, и без оговорки первый же пиксельный расход заведут багом.
 *
 * Плоский объект вместо формы — чтобы компонент тестировался без `Form` и без
 * msw; собирает его страница из `Form.useWatch`.
 */
export interface PreviewRobot {
  name: string;
  code: string;
  image_url: string | null;
  accent_color: string | null;
  market: string;
  risk_level: string;
  min_amount: string;
  declared_yield_12m: string;
  /** Уже отфильтрованные по `visible` и в порядке показа. */
  assets: string[];
  ai_signals: string[];
  users_count: number;
  profit_fee_pct: string;
  tagline: string | null;
  cta_text: string | null;
}

const MAX_ASSETS = 5;

export function RobotCardPreview({ robot }: { robot: PreviewRobot }) {
  const { t } = useTranslation();
  const accent = robot.accent_color ?? SECTIONS.robots.color;
  const shown = robot.assets.slice(0, MAX_ASSETS);
  const rest = robot.assets.length - shown.length;

  return (
    <SectionCard
      color={SECTIONS.robots.color}
      title={t("robots.cardPreview")}
      icon={<RobotOutlined aria-hidden />}
    >
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        {t("robots.cardPreviewHint")}
      </Typography.Paragraph>

      <div
        style={{
          border: `1px solid ${accent}`,
          borderRadius: 16,
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <Space size={10} align="start">
          <Avatar
            size={36}
            shape="square"
            src={robot.image_url ?? undefined}
            style={{
              background: sectionSurface(accent),
              color: accent,
              borderRadius: 12,
              flex: "none",
            }}
            icon={<RobotOutlined aria-hidden />}
          />
          <Space orientation="vertical" size={0}>
            <Typography.Text strong>{robot.name || robot.code}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t(`robots.market_${robot.market}`)} · {t(`robots.risk_${robot.risk_level}`)}
            </Typography.Text>
          </Space>
        </Space>

        {robot.tagline ? (
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {robot.tagline}
          </Typography.Text>
        ) : null}

        <Space orientation="vertical" size={0}>
          <Typography.Text
            strong
            style={{
              fontSize: 24,
              color:
                Number(robot.declared_yield_12m) < 0
                  ? "var(--ant-color-error)"
                  : "var(--ant-color-success)",
            }}
          >
            {formatPercent(robot.declared_yield_12m, { sign: "always" })}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            12M historical
          </Typography.Text>
        </Space>

        {shown.length > 0 ? (
          <Space size={4} wrap>
            {shown.map((symbol) => (
              <Tag key={symbol} style={{ marginInlineEnd: 0 }}>
                {symbol}
              </Tag>
            ))}
            {rest > 0 ? <Tag style={{ marginInlineEnd: 0 }}>+{rest}</Tag> : null}
          </Space>
        ) : null}

        <Space size={12} wrap style={{ fontSize: 12 }}>
          <Typography.Text strong style={{ fontSize: 12 }}>
            {t("robots.minAmountPreview", {
              amount: formatMoney(robot.min_amount || "0", { suffix: "USDT" }),
            })}
          </Typography.Text>
          {robot.ai_signals.length > 0 ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              <ThunderboltOutlined aria-hidden /> AI Market Analysis
            </Typography.Text>
          ) : null}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t("robots.usersPreview", { count: robot.users_count })}
          </Typography.Text>
          {Number(robot.profit_fee_pct) > 0 ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t("robots.feePreview", { pct: formatPercent(robot.profit_fee_pct) })}
            </Typography.Text>
          ) : null}
        </Space>

        <Typography.Text strong style={{ color: accent, fontSize: 13 }}>
          {robot.cta_text || t("robots.ctaFallback")} →
        </Typography.Text>
      </div>
    </SectionCard>
  );
}
